import { useState, useEffect, useCallback } from 'react';
import { 
  isStandalone, 
  isPWAMode, 
  isBrowserMode, 
  getDisplayMode,
  isPWAInstallSupported,
  isIOS, 
  isAndroid, 
  isMobileDevice,
  isIncognito
} from '../utils/deviceDetect';

declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  }
  interface Window {
    deferredPrompt: BeforeInstallPromptEvent | null;
    userGesture: boolean;
  }
}

export function useEnhancedPWAInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOSDevice] = useState(isIOS);
  const [isAndroidDevice] = useState(isAndroid);
  const [isMobile] = useState(isMobileDevice);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [displayMode, setDisplayMode] = useState<string>('browser');
  const [installSupported, setInstallSupported] = useState(false);
  const [isIncognitoMode, setIsIncognitoMode] = useState(false);
  const [userEngagement, setUserEngagement] = useState(false);

  // Track user engagement
  useEffect(() => {
    const trackEngagement = () => {
      setUserEngagement(true);
      (window as any).userGesture = true;
      console.log('👆 User engagement detected');
    };

    const events = ['click', 'scroll', 'keydown', 'touchstart', 'mousedown'];
    events.forEach(event => {
      document.addEventListener(event, trackEngagement, { once: true, passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, trackEngagement);
      });
    };
  }, []);

  // Check installation status
  useEffect(() => {
    const checkInstallationStatus = async () => {
      const installed = isStandalone();
      const currentDisplayMode = getDisplayMode();
      const supported = isPWAInstallSupported();
      const incognito = await isIncognito();
      
      setIsInstalled(installed);
      setDisplayMode(currentDisplayMode);
      setInstallSupported(supported && !incognito);
      setIsIncognitoMode(incognito);
      
      console.log('🔍 PWA Status Check:', {
        installed,
        displayMode: currentDisplayMode,
        isPWAMode: isPWAMode(),
        isBrowserMode: isBrowserMode(),
        installSupported: supported && !incognito,
        isMobile: isMobile,
        isIncognitoMode: incognito,
        userEngagement
      });
      
      // Show banner if conditions are met
      if (!installed && !installDismissed && isMobile && !incognito && userEngagement) {
        const timer = setTimeout(() => {
          setShowInstallBanner(true);
        }, 2000);
        
        return () => clearTimeout(timer);
      }
    };

    checkInstallationStatus();
  }, [installDismissed, isMobile, userEngagement]);

  // Handle beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('🔔 beforeinstallprompt event fired');
      
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setInstallPrompt(promptEvent);
      setCanInstall(true);
      (window as any).deferredPrompt = promptEvent;
      
      console.log('🔔 Install prompt available:', {
        platforms: (promptEvent as any).platforms,
        userChoice: promptEvent.userChoice
      });
      
      // Show banner if user has engaged
      if (userEngagement && !installDismissed) {
        setShowInstallBanner(true);
      }
    };

    const handleAppInstalled = () => {
      console.log('✅ PWA was installed successfully');
      setIsInstalled(true);
      setCanInstall(false);
      setInstallPrompt(null);
      setShowInstallBanner(false);
      (window as any).deferredPrompt = null;
    };

    // Listen for custom events from index.html
    const handleCustomInstallAvailable = (e: CustomEvent) => {
      console.log('🔔 Custom install available event received');
      setCanInstall(true);
      if (userEngagement && !installDismissed) {
        setShowInstallBanner(true);
      }
    };

    const handleCustomInstalled = (e: CustomEvent) => {
      console.log('✅ Custom installed event received');
      setIsInstalled(true);
      setCanInstall(false);
      setShowInstallBanner(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwa-install-available', handleCustomInstallAvailable as EventListener);
    window.addEventListener('pwa-installed', handleCustomInstalled as EventListener);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwa-install-available', handleCustomInstallAvailable as EventListener);
      window.removeEventListener('pwa-installed', handleCustomInstalled as EventListener);
    };
  }, [userEngagement, installDismissed]);

  // Enhanced install click handler with forced native prompt
  const handleInstallClick = useCallback(async () => {
    console.log('🚀 Enhanced install click triggered', { 
      isIOSDevice, 
      installPrompt: !!installPrompt, 
      canInstall,
      deferredPrompt: !!(window as any).deferredPrompt,
      isInstalled,
      isPWAMode: isPWAMode(),
      isBrowserMode: isBrowserMode(),
      userEngagement
    });

    // Ensure user engagement is marked
    setUserEngagement(true);
    (window as any).userGesture = true;

    // Check if already installed
    if (isInstalled || isPWAMode()) {
      console.log('✅ App is already installed');
      return true;
    }

    // Force trigger beforeinstallprompt event first
    console.log('🔄 Forcing beforeinstallprompt event trigger');
    const beforeInstallEvent = new Event('beforeinstallprompt', { bubbles: true });
    window.dispatchEvent(beforeInstallEvent);
    
    // Also try custom event
    const customEvent = new CustomEvent('beforeinstallprompt', { 
      detail: { platforms: ['web'] },
      bubbles: true 
    });
    window.dispatchEvent(customEvent);

    // Wait for event to be processed
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Try to use the stored prompt or newly triggered prompt
    let promptToUse = installPrompt || (window as any).deferredPrompt;
    
    if (promptToUse) {
      try {
        console.log('🚀 Showing native install prompt');
        await promptToUse.prompt();
        const { outcome } = await promptToUse.userChoice;
        
        console.log('📊 Install prompt outcome:', outcome);
        
        if (outcome === 'accepted') {
          console.log('✅ User accepted the install prompt');
          setIsInstalled(true);
          setCanInstall(false);
          setInstallPrompt(null);
          setShowInstallBanner(false);
          (window as any).deferredPrompt = null;
          return true;
        } else {
          console.log('❌ User dismissed the install prompt');
          return false;
        }
      } catch (error) {
        console.error('❌ Error showing install prompt:', error);
      }
    }

    // Try alternative installation methods
    console.log('🔄 Trying alternative installation methods...');
    
    // Method 1: Try to create a temporary button and click it
    try {
      const tempButton = document.createElement('button');
      tempButton.style.position = 'fixed';
      tempButton.style.top = '-1000px';
      tempButton.style.left = '-1000px';
      tempButton.style.width = '1px';
      tempButton.style.height = '1px';
      tempButton.style.opacity = '0';
      tempButton.textContent = 'Install App';
      document.body.appendChild(tempButton);
      
      // Add event listener to capture beforeinstallprompt
      const installHandler = (e: any) => {
        e.preventDefault();
        (window as any).deferredPrompt = e;
        console.log('🔔 Captured beforeinstallprompt from temp button');
      };
      
      window.addEventListener('beforeinstallprompt', installHandler, { once: true });
      
      // Simulate user interaction
      tempButton.click();
      
      // Wait for event
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Remove temp button
      document.body.removeChild(tempButton);
      window.removeEventListener('beforeinstallprompt', installHandler);
      
      // Try to use the captured prompt
      const capturedPrompt = (window as any).deferredPrompt;
      if (capturedPrompt) {
        console.log('🚀 Using captured install prompt');
        await capturedPrompt.prompt();
        const { outcome } = await capturedPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('✅ User accepted the captured prompt');
          setIsInstalled(true);
          setCanInstall(false);
          setInstallPrompt(null);
          setShowInstallBanner(false);
          (window as any).deferredPrompt = null;
          return true;
        }
      }
    } catch (error) {
      console.error('❌ Alternative method 1 failed:', error);
    }

    // Method 2: Try to trigger through user gesture simulation
    try {
      console.log('🔄 Trying user gesture simulation...');
      
      // Create a more realistic user interaction
      const userGestureEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      
      // Dispatch on document to simulate user interaction
      document.dispatchEvent(userGestureEvent);
      
      // Also try touch event for mobile
      if (isMobile) {
        const touchEvent = new TouchEvent('touchstart', {
          bubbles: true,
          cancelable: true,
          touches: [new Touch({
            identifier: 0,
            target: document.body,
            clientX: 0,
            clientY: 0,
            screenX: 0,
            screenY: 0,
            pageX: 0,
            pageY: 0
          })]
        });
        document.dispatchEvent(touchEvent);
      }
      
      // Wait for beforeinstallprompt
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const gesturePrompt = (window as any).deferredPrompt;
      if (gesturePrompt) {
        console.log('🚀 Using gesture-triggered prompt');
        await gesturePrompt.prompt();
        const { outcome } = await gesturePrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('✅ User accepted the gesture-triggered prompt');
          setIsInstalled(true);
          setCanInstall(false);
          setInstallPrompt(null);
          setShowInstallBanner(false);
          (window as any).deferredPrompt = null;
          return true;
        }
      }
    } catch (error) {
      console.error('❌ Alternative method 2 failed:', error);
    }

    // Method 3: For iOS, try to trigger the native prompt
    if (isIOSDevice) {
      console.log('🍎 iOS device detected, trying native prompt trigger');
      try {
        // iOS Safari sometimes needs a specific user interaction
        const iosEvent = new Event('beforeinstallprompt', { bubbles: true });
        Object.defineProperty(iosEvent, 'prompt', {
          value: async () => {
            console.log('🍎 iOS install prompt triggered');
            // On iOS, we can't actually prompt, but we can show instructions
            return Promise.resolve();
          }
        });
        Object.defineProperty(iosEvent, 'userChoice', {
          value: Promise.resolve({ outcome: 'dismissed' })
        });
        
        window.dispatchEvent(iosEvent);
        
        // For iOS, we need to show instructions as fallback
        console.log('🍎 iOS: Showing installation instructions');
        return false;
      } catch (error) {
        console.error('❌ iOS prompt trigger failed:', error);
        return false;
      }
    }

    console.log('❌ All installation methods failed');
    return false;
  }, [installPrompt, isInstalled, isIOSDevice, isMobile]);

  // Dismiss install banner
  const dismissInstallBanner = useCallback(() => {
    setShowInstallBanner(false);
    setInstallDismissed(true);
  }, []);

  // Trigger install prompt manually
  const triggerInstallPrompt = useCallback(() => {
    console.log('🔄 Manually triggering install prompt');
    const event = new Event('beforeinstallprompt');
    window.dispatchEvent(event);
    
    if ((window as any).deferredPrompt) {
      console.log('✅ Found deferred prompt, setting canInstall to true');
      setInstallPrompt((window as any).deferredPrompt);
      setCanInstall(true);
    }
  }, []);

  // Mobile-specific install trigger
  const triggerMobileInstall = useCallback(async () => {
    console.log('📱 Triggering mobile-specific install methods');
    if (isMobile) {
      try {
        // Create a temporary button to simulate user interaction
        const installButton = document.createElement('button');
        installButton.style.position = 'fixed';
        installButton.style.top = '-1000px';
        installButton.style.left = '-1000px';
        installButton.style.width = '1px';
        installButton.style.height = '1px';
        installButton.style.opacity = '0';
        installButton.textContent = 'Install App';
        document.body.appendChild(installButton);
        
        // Simulate user interaction
        installButton.click();
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if ((window as any).deferredPrompt) {
          console.log('📱 Mobile install prompt triggered');
          setInstallPrompt((window as any).deferredPrompt);
          setCanInstall(true);
        }
        
        document.body.removeChild(installButton);
      } catch (error) {
        console.error('❌ Mobile install trigger failed:', error);
      }
    }
  }, [isMobile]);

  return {
    // State
    isInstalled,
    canInstall,
    isIOSDevice,
    isAndroidDevice,
    isMobile,
    isIncognito: isIncognitoMode,
    showInstallBanner,
    displayMode,
    installSupported,
    userEngagement,
    
    // Actions
    handleInstallClick,
    dismissInstallBanner,
    triggerInstallPrompt,
    triggerMobileInstall,
    
    // Utilities
    isPWAMode: isPWAMode(),
    isBrowserMode: isBrowserMode()
  };
}
